<template>
  <v-bottom-sheet v-model="showProfileSheet" persistent fullscreen scrollable class="profile-sheet-main">
    <v-sheet class="profile-sheet">
      <div class="profile-sheet-wrapper">
        <div class="profile-sheet-header">
          <div class="profile-sheet-header-wrapper">
            <div class="header-member-name">
              <v-avatar size="64" class="profile-sheet-profile-avatar">
                {{ memberInfo.name ? memberInfo.name.slice(0, 1).toUpperCase() : memberInfo.memberCode.slice(0, 1).toUpperCase() }}
              </v-avatar>
              <div class="header-member-text">
                <div class="header-member-username">
                  {{ memberInfo.name || memberInfo.memberCode }}
                  <CopyBtn :value="memberInfo.memberCode" />
                </div>
                <span v-if="tierName" class="header-member-tier">{{ tierName }}</span>
              </div>
            </div>
            <button type="button" class="profile-sheet-close-wrapper" aria-label="Close" @click="closeProfileSheet">
              <v-icon color="#ffffff">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="profile-sheet-body">
          <div class="profile-sheet-body-wrapper">
            <!-- Wallet first: it is what members open this sheet for -->
            <div class="profile-sheet-wallet">
              <div class="profile-sheet-balance">
                <label>{{ $t(`label.mainWallet`) }}</label>
                <div class="balance" v-if="notLoading">
                  <span v-if="showBalance">{{ memberWallet.totalBalance | currency(currentCurrency) | maskNumber }}</span>
                  <span v-else>{{ memberWallet.totalBalance | currency(currentCurrency) }}</span>
                </div>
                <v-progress-circular indeterminate v-else color="#ff7700" :width="3" :size="24"></v-progress-circular>
              </div>
              <div class="profile-sheet-wallet-actions">
                <button type="button" class="ps-icon-btn" aria-label="Refresh balance" @click="getMemberWallet()">
                  <span class="refresh-balance-icon"></span>
                </button>
                <button type="button" class="ps-icon-btn" aria-label="Show or hide balance" @click="maskBalance()">
                  <v-icon size="20">{{ showBalance ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                </button>
              </div>
            </div>

            <div v-if="isLoggedIn" class="ps-vip" :class="[tierColorThemeName]">
              <div class="ps-vip-main">
                <div class="ps-vip-info">
                  <div class="ps-vip-level">
                    {{ $t(`label.viptierlevel`) }}:
                    <b>{{ tierName }}</b>
                  </div>
                  <div class="ps-vip-progress">
                    <div class="ps-vip-progress-head">
                      <span>{{ $t('label.deposit') }}</span>
                      <span>{{ currentDeposit }} / {{ requiredDeposit }}</span>
                    </div>
                    <v-progress-linear class="ps-vip-bar" :class="[tierColorThemeName]" :value="currentProgressPercentage" height="8" rounded></v-progress-linear>
                  </div>
                  <div class="ps-vip-progress">
                    <div class="ps-vip-progress-head">
                      <span>{{ $t('label.turnover') }}</span>
                      <span>{{ currentTurnover }} / {{ requiredTurnover }}</span>
                    </div>
                    <v-progress-linear class="ps-vip-bar" :class="[tierColorThemeName]" :value="currentTurnoverProgressPercentage" height="8" rounded></v-progress-linear>
                  </div>
                  <div class="ps-vip-next">
                    <span v-if="tierColorThemeName === 'black'">{{ $t('label.highestlevelreach') }}</span>
                    <template v-else>
                      <b>{{ $t('label.upgradetonextlevel') }} {{ nextlevelname }} {{ $t('label.nextlevel') }}</b>
                      <span>{{ $t('label.depositRequired') }}{{ progressRequired }}</span>
                      <span>{{ $t('label.turnoverRequired') }}{{ turnoverRequired }}</span>
                    </template>
                  </div>
                </div>
                <img class="ps-vip-badge" :src="`/static/svg/vipassets/${tierColorThemeName}_vip.png`" :alt="tierName" />
              </div>
              <button type="button" class="ps-vip-link" @click="goToTierDetails">
                {{ $t(`label.viewMemberShip`) }}
                <v-icon size="20">mdi-chevron-right</v-icon>
              </button>
            </div>

            <div class="guideSectionProfile" v-if="showVoucherGuide">
              <v-icon size="20" class="guide-icon">mdi-play-circle-outline</v-icon>
              <label>
                {{ $t(`label.voucherGuide`) }} {{ $t(`label.clickThis`) }}
                <a href="https://ng88.wistia.com/s/ybqg20udm71jegd" target="_blank" rel="noopener" class="linkText">{{ $t(`label.link`) }}</a>
                {{ $t(`label.watchVideo`) }}
              </label>
              <button type="button" class="ps-guide-close" aria-label="Close" @click="closeShowVoucherGuide()">
                <v-icon size="18">mdi-close</v-icon>
              </button>
            </div>

            <button type="button" class="ps-referral" @click="goToReferral">
              <img src="/static/image/profile/profile-referral.png" width="36" height="36" alt="" />
              <span>{{ $t(`label.referralRedirect`) }}</span>
              <v-icon color="#ffffff">mdi-chevron-right</v-icon>
            </button>
            <div class="profile-sheet-item">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.fund`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <div class="profile-sheet-item-column" @click="routeTo('DEPOSIT')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-deposit.png" width="39" height="39" alt="deposit icon" />
                  </div>
                  <label>{{ $t(`label.deposit`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('WITHDRAWAL')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-withdraw.png" width="39" height="39" alt="withdrawal icon" />
                  </div>
                  <label>{{ $t(`label.withdrawal`) }}</label>
                </div>
                <div v-if="false" class="profile-sheet-item-column" @click="routeTo('REBATE')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-bonus.png" width="39" height="39" alt="rebate" />
                  </div>
                  <label>{{ $t(`label.InstantRebate`) }}</label>
                </div>
              </div>
            </div>
            <div class="profile-sheet-item">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.history`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <div class="profile-sheet-item-column" @click="routeTo('BETTING_RECORD')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-betting-record.png" width="39" height="39" alt="total icon" />
                  </div>
                  <label>{{ $t(`label.bettingRecors`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('TURNOVER_RECORD')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-turnover.png" width="39" height="39" alt="total icon" />
                  </div>
                  <label>{{ $t(`label.turnoverRecors`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('TRANSFER_RECORD')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-transfer-record.png" width="39" height="39" alt="transfer icon" />
                  </div>
                  <label>{{ $t(`label.transferRecors`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('BONUS')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-bonus.png" width="39" height="39" alt="bonus icon" />
                  </div>
                  <label>{{ $t(`label.bonus`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('TRANSACTION')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-transaction-record.png" width="39" height="39" alt="transaction icon" />
                  </div>
                  <label>{{ $t(`label.transactionRecors`) }}</label>
                </div>
              </div>
            </div>
            <div class="profile-sheet-item">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.rewards`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <!-- <div class="profile-sheet-item-column" @click="routeTo('DAILY_CHECK_IN')">

                </div> -->
                <div class="profile-sheet-item-column" @click="routeTo('DEPOSIT')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/icon-depositfreecoin.png" width="39" height="39" alt="checkin icon" />
                  </div>
                  <label>{{ $t(`label.depositgetfreecoin`) }}</label>
                </div>

                <div class="profile-sheet-item-column" @click="routeTo('REWARDS')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/icon_Daily%20Check%20In.png" width="39" height="39" alt="checkin icon" />
                  </div>
                  <label>{{ $t(`label.dailyCheckIn`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="$vuetify.breakpoint.mdAndDown ? routeTo('MWOF') : routeTo('WOF')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/icon-wof.png" width="39" height="39" alt="lucky spin" />
                  </div>
                  <label>{{ $t(`label.fortuneSpin`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('CLAIM_VOUCHER')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/icon_Claim%20Voucher.png" width="39" height="39" alt="grabcoin icon" />
                  </div>
                  <label>{{ $t(`label.claimVoucher`) }}</label>
                </div>
                <!--                <div disabled class="profile-sheet-item-column disbaledDiv">-->
                <!--                  <div class="profile-sheet-icon">-->
                <!--                    <img src="/static/image/profile/icon_Coin%20Carnival.png" alt="coin carnival" />-->
                <!--                  </div>-->
                <!--                  <label>{{ $t(`label.coinCarnival`) }}</label>-->
                <!--                </div>-->
              </div>
            </div>
            <div class="profile-sheet-item">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.profile`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <div class="profile-sheet-item-column" @click="routeTo('PERSONAL')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-personal-info.png" width="39" height="39" alt="info icon" />
                  </div>
                  <label>{{ $t(`label.personalInfo`) }}</label>
                </div>
                <div v-if="!telegramLogin" class="profile-sheet-item-column" @click="routeTo('CHANGE_PASSWORD')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-change-password.png" width="39" height="39" alt="reset icon" />
                  </div>
                  <label>{{ $t(`label.changePassword`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('BANK_DETAIL')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-bank-details.png" width="39" height="39" alt="info icon" />
                  </div>
                  <label>{{ $t(`label.bankDetail`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('INBOX')">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-inbox-message.png" width="39" height="39" alt="inbox icon" />
                  </div>
                  <label>{{ $t(`label.inbox`) }}</label>
                </div>
                <!--                <div class="profile-sheet-item-column" @click="routeTo('REFERRAL_SUMMARY')">-->
                <!--                  <div class="profile-sheet-icon">-->
                <!--                    <img src="/static/image/profile/profile-referral.png" width="39" height="39" alt="referral" />-->
                <!--                  </div>-->
                <!--                  <label>{{ $t(`label.referral`) }}</label>-->
                <!--                </div>-->
                <!--                <div class="profile-sheet-item-column" v-if="affiliateBonus.status == 1"-->
                <!--                     @click="routeTo('REFERRAL_BONUS')">-->
                <!--                  <div class="profile-sheet-icon">-->
                <!--                    <img src="/static/image/profile/profile-referral-bonus.png" width="39" height="39" alt="referral icon" />-->
                <!--                  </div>-->
                <!--                  <label>{{ $t(`label.referralBonus`) }}</label>-->
                <!--                </div>-->
              </div>
            </div>
            <div class="profile-sheet-item">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.contactUs`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <div class="profile-sheet-item-column" @click="showIntercomLC()">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-supports.png" width="39" height="39" alt="support icon" @click="showIntercomLC()" />
                  </div>
                  <label>{{ $t(`label.support`) }}</label>
                </div>
                <!--                <div disabled class="profile-sheet-item-column disbaledDiv" @click="routeTo('WHATSAPP', true)">-->
                <!--                  <div class="profile-sheet-icon">-->
                <!--                    <img src="/static/image/profile/icon-whatsapp.svg" alt="whatsapp icon"/>-->
                <!--                  </div>-->
                <!--                  <label>{{ $t(`label.whatsApp`) }}</label>-->
                <!--                </div>-->
                <div class="profile-sheet-item-column" @click="routeTo('TELEGRAM', true)">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/icon-telegram.png" width="39" height="39" alt="telegram icon" />
                  </div>
                  <label>{{ $t(`label.telegram`) }}</label>
                </div>
                <div class="profile-sheet-item-column" @click="routeTo('FACEBOOK', true)">
                  <div class="profile-sheet-icon">
                    <img src="/static/image/profile/profile-fb.png" width="39" height="39" alt="facebook icon" />
                  </div>
                  <label>{{ $t(`label.facebook`) }}</label>
                </div>
              </div>
            </div>
            <div class="profile-sheet-item" v-if="(isAndroid || isDesktop) && showDownload">
              <div class="profile-sheet-item-header">
                <div class="profile-sheet-item-title">{{ $t(`label.download`) }}</div>
              </div>
              <div class="profile-sheet-item-divider"></div>
              <div :class="$vuetify.breakpoint.mdAndDown ? 'profile-sheet-item-row-mobile' : 'profile-sheet-item-row'">
                <div class="profile-sheet-item-column" @click="downloadAPK">
                  <div class="profile-sheet-icon">
                    <img width="39" height="39" src="/static/image/profile/profile-logout.png" alt="download apk icon" />
                  </div>
                  <label>{{ $t(`label.downloadandroid`) }}</label>
                </div>
              </div>
            </div>
            <div class="logout-container">
              <div class="logout-btn" @click="logoutBtn">
                <div class="logout-icon"></div>
                <div>{{ $t(`label.logout`) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-sheet>
  </v-bottom-sheet>
</template>

<script>
import { ROUTE_NAME } from '@/constants/route.constants'
import { MEMBER_DETAIL, MEMBER_WALLET, MEMBER_VIP_TIER_PROGRESS, MEMBER_GET_VIP_PROGRESS, MEMBER_VIP_TIER_COMMUNICATION, MEMBER_RESET_VIP_TIER_COMMUNICATION } from '@/store/member.module'
import { locale, errorCodeHelper, uiHelper } from '@/util'
import { AFFILIATE_ONE_TIME_BONUS_DETAIL } from '@/store/affiliate.bonus.module'
import { SHARED } from '@/constants/constants'
import CopyBtn from '@/components/member/CopyBtn.vue'

export default {
  name: 'ProfileSheet',
  components: { CopyBtn },
  props: {
    showProfileSheet: {
      type: Boolean,
      required: true,
      default: false
    },
    closeProfileSheet: {
      type: Function,
      required: false
    },
    logout: {
      type: Function,
      required: false
    }
  },
  data: () => ({
    showVoucherGuide: true,
    currentDeposit: 0,
    requiredDeposit: 0,
    currentTurnover: 0,
    requiredTurnover: 0,
    currentTurnoverProgressPercentage: 0,
    showProgress: false,
    tierBarBgColor: '',
    tierBarColor: '',
    tierColorThemeName: 'starter',
    tierName: '',
    currentProgressPercentage: 0,
    currentLv: 0,
    nextLv: 0,
    progressRequired: 0,
    turnoverRequired: 0,
    nextlevelname: '',
    routeName: ROUTE_NAME,
    notLoading: true,
    currentCurrency: uiHelper.getCurrency(),
    showBalance: true,
    isAndroid: uiHelper.isAndroid(),
    isDesktop: uiHelper.getPlatform() == 'desktop'
  }),
  created() {
    if (this.isLoggedIn) {
      this.getMemberDetail()
      this.getVipTierProgress()
      this.getVipProgress()
    }
  },
  computed: {
    vipTierUpgrade() {
      return this.$store.state.member.vipTierProgress.upgrade
    },
    memberVipTierProgress() {
      return this.$store.state.member.vipTierProgress.pairing
    },
    vipProgress() {
      return this.$store.state.member.vipProgress
    },
    isLoggedIn() {
      return this.$store.state.member.isLoggedIn
    },
    memberInfo() {
      return this.$store.state.member.info
    },
    memberWallet() {
      return this.$store.state.member.walletBalance
    },
    affiliateBonus() {
      return this.$store.state.affiliateBonus.affiliateBonusDetail
    },
    currentLvlName() {
      return this.vipProgress.current_level_name
    },
    communicationLink() {
      return this.$store.state.member.vipTierCommunication.success
    },
    telegramLogin() {
      return this.$store.state.member.info.defaultRegType == SHARED.LOGIN_TYPE_TELEGRAM
    },
    showDownload() {
      return uiHelper.shouldShowDownload(this.$store.state.siteSettings.hideDownloadBar)
    }
  },

  methods: {
    showIntercomLC() {
      Intercom('show')
      return false
    },
    closeShowVoucherGuide() {
      this.showVoucherGuide = false
    },
    openLiveChat() {
      window.open('https://direct.lc.chat/15455814/', 'Live Chat', 'width=350,height=600')
    },
    getVipProgress() {
      if (this.isLoggedIn) {
        this.$store.dispatch(MEMBER_GET_VIP_PROGRESS)
      }
    },
    goToTierDetails() {
      this.$router.push({ name: this.routeName.VIPTIER })
      this.closeProfileSheet()
    },
    goToReferral() {
      this.$router.push({ name: this.routeName.REFERRAL_SUMMARY })
      this.closeProfileSheet()
    },
    getVipTierProgress() {
      this.$store.dispatch(MEMBER_VIP_TIER_PROGRESS)
    },
    getAffiliateBonusDetail() {
      this.$store.dispatch(`${AFFILIATE_ONE_TIME_BONUS_DETAIL}`)
    },
    maskBalance() {
      this.showBalance = !this.showBalance
    },
    getMemberDetail() {
      this.$store.dispatch(`${MEMBER_DETAIL}`)
    },
    getMemberWallet() {
      const lastDispatchTime = sessionStorage.getItem('lastDispatchTime')

      if (!lastDispatchTime || Date.now() - parseInt(lastDispatchTime) > 15 * 1000) {
        this.notLoading = false
        sessionStorage.setItem('lastDispatchTime', Date.now()) // Update last dispatch time in sessionStorage
        this.$store.dispatch(MEMBER_WALLET)
      }

      this.maskBalance()
    },
    getVipCommunicationLink(type) {
      let communicationUrl = {
        type: type
      }

      this.$store.dispatch(`${MEMBER_VIP_TIER_COMMUNICATION}`, { communicationUrl })
    },
    logoutBtn() {
      this.logout()
      this.closeProfileSheet()
    },
    routeTo(routeTo, extLink) {
      let params
      this.closeProfileSheet()
      switch (routeTo) {
        case 'DEPOSIT':
          // routeTo = ROUTE_NAME.DEPOSIT

          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.FUNDS
            params = {
              tab: 'deposit',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.FUNDS
            params = {
              tab: 'deposit'
            }
          }
          break
        case 'WITHDRAWAL':
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.FUNDS
            params = {
              tab: 'withdrawal',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.FUNDS
            params = {
              tab: 'withdrawal'
            }
          }
          break
        case 'TRANSFER_RECORD':
          // routeTo = ROUTE_NAME.TRANSFER_HISTORY;
          // params = {
          //   tab: "TRANSFER"
          // };
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.TRANSFER_HISTORY
            params = {
              tab: 'TRANSFER',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.TRANSFER_HISTORY
            params = {
              tab: 'TRANSFER'
            }
          }
          break
        case 'BETTING_RECORD':
          // routeTo = ROUTE_NAME.BETTING_HISTORY;
          // params = {
          //   tab: "TRANSFER"
          // };
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.BETTING_HISTORY
            params = {
              tab: 'TRANSFER',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.BETTING_HISTORY
            params = {
              tab: 'TRANSFER'
            }
          }
          break
        case 'TURNOVER_RECORD':
          // routeTo = ROUTE_NAME.TURNOVER_RECORD;
          // params = {
          //   tab: "RUNNING"
          // };
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.TURNOVER_RECORD
            params = {
              tab: 'RUNNING',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.TURNOVER_RECORD
            params = {
              tab: 'RUNNING'
            }
          }
          break
        case 'BONUS':
          // routeTo = ROUTE_NAME.BONUS_HISTORY;
          // params = {
          //   tab: "REBATE"
          // };
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.BONUS_HISTORY
            params = {
              tab: 'REBATE',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.BONUS_HISTORY
            params = {
              tab: 'REBATE'
            }
          }
          break
        case 'TRANSACTION':
          // routeTo = ROUTE_NAME.TRANSACTION_HISTORY;
          // params = {
          //   tab: "DEPOSIT"
          // };
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.TRANSACTION_HISTORY
            params = {
              tab: 'DEPOSIT',
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.TRANSACTION_HISTORY
            params = {
              tab: 'DEPOSIT'
            }
          }
          break
        case 'PERSONAL':
          // routeTo = ROUTE_NAME.PERSONAL;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.PERSONAL
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.PERSONAL
          }
          break
        case 'BANK_DETAIL':
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.BANK_ACCOUNT
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.BANK_ACCOUNT
          }
          break
        case 'CHANGE_PASSWORD':
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.CHANGE_PASSWORD
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.CHANGE_PASSWORD
          }
          // routeTo = ROUTE_NAME.CHANGE_PASSWORD;
          break
        case 'INBOX':
          // routeTo = ROUTE_NAME.INBOX;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.INBOX
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.INBOX
          }
          break
        case 'REFERRAL_SUMMARY':
          // routeTo = ROUTE_NAME.REFERRAL_SUMMARY;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.REFERRAL_SUMMARY
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.REFERRAL_SUMMARY
          }
          break
        case 'REFERRAL_BONUS':
          // routeTo = ROUTE_NAME.REFERRAL_BONUS;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.REFERRAL_BONUS
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.REFERRAL_BONUS
          }
          break
        case 'REWARDS':
          // routeTo = ROUTE_NAME.REWARDS;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.REWARDS
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.REWARDS
          }
          break
        case 'DAILYCHECKIN':
          // routeTo = ROUTE_NAME.DAILYCHECKIN;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.DAILYCHECKIN
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.DAILYCHECKIN
          }
          break
        case 'CLAIM_VOUCHER':
          //   routeTo = ROUTE_NAME.CLAIM_VOUCHER;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.CLAIM_VOUCHER
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.CLAIM_VOUCHER
          }
          break
        case 'REBATE':
          // routeTo = ROUTE_NAME.REBATE;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.REBATE
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.REBATE
          }
          break
        case 'APPLY_AGENT':
          // routeTo = ROUTE_NAME.APPLY_AGENT;
          if (this.$route.params.lang) {
            routeTo = ROUTE_NAME.APPLY_AGENT
            params = {
              lang: this.$route.params.lang
            }
          } else {
            routeTo = ROUTE_NAME.APPLY_AGENT
          }
          break
        case 'MWOF':
          routeTo = ROUTE_NAME.MWOF
          params = {
            lang: this.$route.params.lang
          }
          break
        case 'WOF':
          routeTo = ROUTE_NAME.WOF
          params = {
            lang: this.$route.params.lang
          }
          break
        case 'COIN_CARNIVAL':
          // routeTo = ROUTE_NAME.MWOF
          break
      }

      if (extLink == undefined || !extLink) {
        this.$router.push({
          name: routeTo,
          params
        })
      } else {
        /*external link */
        if (routeTo == 'FACEBOOK') {
          this.getVipCommunicationLink(routeTo)
          // window.open('https://www.facebook.com/nagad88official')
        } else if (routeTo == 'WHATSAPP') {
          this.getVipCommunicationLink(routeTo)
          // window.open('https://bit.ly/NAGAD88_CustomerSupport', '_self')
        } else if (routeTo == 'TELEGRAM') {
          this.getVipCommunicationLink(routeTo)
          // window.open('https://t.me/nagad88official')
        } else if (routeTo == 'LIVECHAT') {
          window.open('mailto:nagad88help@gmail.com/')
        }
      }
    },
    downloadAPK() {
      uiHelper.downloadAPK()
    }
  },
  watch: {
    vipTierUpgrade() {
      if (Object.keys(this.vipTierUpgrade).length > 0) {
        if (this.vipTierUpgrade.current_upgrade_deposit > 0) {
          this.currentProgressPercentage = Math.ceil((this.vipTierUpgrade.current_upgrade_deposit / this.vipTierUpgrade.required_upgrade_deposit) * 100)
        } else {
          this.currentProgressPercentage = 0
        }

        this.progressRequired = this.vipTierUpgrade.required_upgrade_deposit - this.vipTierUpgrade.current_upgrade_deposit
        this.nextlevelname = this.vipTierUpgrade.vip_level_name
      }
      let required = Number(this.vipTierUpgrade.required_upgrade_deposit).toLocaleString()
      required = required.replace(/\.00$/, '')
      let current = Number(this.vipTierUpgrade.current_upgrade_deposit).toLocaleString()
      current = current.replace(/\.00$/, '')
      this.currentDeposit = current

      this.requiredDeposit = required
      this.progressRequired = this.vipTierUpgrade.required_upgrade_deposit - this.vipTierUpgrade.current_upgrade_deposit
      if (this.progressRequired <= 0) {
        this.progressRequired = 'Completed'
      } else {
        this.progressRequired = ' ৳ ' + Number(this.progressRequired).toLocaleString()
      }
      this.nextlevelname = this.vipTierUpgrade.vip_level_name.toLowerCase().replace(/^(.)(.*)$/, function(_, firstLetter, restOfWord) {
        return firstLetter.toUpperCase() + restOfWord.toLowerCase()
      })
      this.turnoverRequired = this.vipTierUpgrade.required_upgrade_turnover - this.vipTierUpgrade.current_upgrade_turnover
      if (this.turnoverRequired <= 0) {
        this.turnoverRequired = 'Completed'
      } else {
        this.turnoverRequired = ' ৳ ' + Number(this.turnoverRequired).toLocaleString()
      }

      let requiredTurnover = Number(this.vipTierUpgrade.required_upgrade_turnover).toLocaleString()
      requiredTurnover = requiredTurnover.replace(/\.00$/, '')
      let currentTurnover = Number(this.vipTierUpgrade.current_upgrade_turnover).toLocaleString()
      currentTurnover = currentTurnover.replace(/\.00$/, '')
      this.currentTurnover = currentTurnover
      this.requiredTurnover = requiredTurnover
      if (this.vipTierUpgrade.current_upgrade_turnover > 0) {
        this.currentTurnoverProgressPercentage = Math.min(Math.ceil((this.vipTierUpgrade.current_upgrade_turnover / this.vipTierUpgrade.required_upgrade_turnover) * 100), 100)
      } else {
        this.currentTurnoverProgressPercentage = 0
      }
    },
    memberWallet() {
      this.notLoading = true
    },
    isLoggedIn() {
      this.getAffiliateBonusDetail()
      this.getVipTierProgress()
      this.getMemberDetail()
      this.getVipProgress()
    },
    memberVipTierProgress() {
      let vipTierProgress = this.$store.state.member.vipTierProgress
      if (Object.keys(vipTierProgress.pairing).length > 0) {
        let curPairing = vipTierProgress.pairing
        //progress found
        this.showProgress = true
        switch (curPairing.level_sequence) {
          case 0:
            this.tierColorThemeName = 'starter'
            break
          case 1:
            this.tierColorThemeName = 'bronze'
            break
          case 2:
            this.tierColorThemeName = 'silver'
            break
          case 3:
            this.tierColorThemeName = 'gold'
            break
          case 4:
            this.tierColorThemeName = 'platinum'
            break
          case 5:
            this.tierColorThemeName = 'diamond'
            break
          case 6:
            this.tierColorThemeName = 'black'
            break
          default:
            this.tierColorThemeName = 'starter'
            this.tierBarBgColor = '#1867c0'
        }
        this.tierBarBgColor = 'viptier' + this.tierColorThemeName
        this.tierName = curPairing.name
        this.currentLv = this.memberVipTierProgress.level_sequence
        this.nextLv = this.memberVipTierProgress.level_sequence + 1
      } else {
        //no progress
        this.showProgress = false
      }
    },
    communicationLink() {
      if (this.$store.state.member.vipTierCommunication.success) {
        window.open(this.$store.state.member.vipTierCommunication.data)
        this.$store.dispatch(`${MEMBER_RESET_VIP_TIER_COMMUNICATION}`)
      }
    }
  }
}
</script>

<style lang="scss">
.linkText {
  color: #0039f6 !important;
}

// ---- Profile sheet (NG88 heritage) ----
.profile-sheet {
  position: relative;
  background-color: var(--ng-cloud) !important;
  overflow-y: auto;
  overflow-x: hidden;
  font-family: var(--ng-font);
  color: var(--ng-navy);

  .profile-sheet-wrapper {
    min-height: 100%;
  }

  .profile-sheet-header {
    background: var(--ng-gradient-brand);
    padding: 20px 16px 64px;
    border-radius: 0 0 28px 28px;

    .profile-sheet-header-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 720px;
      margin: auto;
    }

    .header-member-name {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 14px;
      color: #ffffff;
    }

    .profile-sheet-profile-avatar {
      flex: none;
      background: #ffffff;
      color: var(--ng-violet);
      font-family: var(--ng-font-bold);
      font-size: 28px;
      box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.25);
    }

    .header-member-text {
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    .header-member-username {
      display: flex;
      align-items: center;
      font-family: var(--ng-font-bold);
      font-size: 20px;
      line-height: 1.2;
      word-break: break-all;

      .copy-btn .v-icon {
        color: rgba(255, 255, 255, 0.8) !important;
      }
    }

    .header-member-tier {
      padding: 2px 10px;
      border-radius: 999px;
      background: var(--ng-yellow);
      color: var(--ng-navy);
      font-family: var(--ng-font-bold);
      font-size: 11px;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }

    .profile-sheet-close-wrapper {
      flex: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.18);
      cursor: pointer;
      transition: background 0.2s ease-out;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }
  }

  .profile-sheet-body {
    margin-top: -48px;
    padding: 0 12px 24px;

    .profile-sheet-body-wrapper {
      max-width: 720px;
      margin: auto;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }

  // Shared card surface
  .profile-sheet-wallet,
  .ps-vip,
  .profile-sheet-item,
  .logout-container {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(18, 23, 52, 0.06);
  }

  // Wallet
  .profile-sheet-wallet {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px;

    .profile-sheet-balance {
      display: flex;
      flex-direction: column;
      gap: 2px;

      label {
        color: var(--ng-text-muted, #5b6080);
        font-size: 13px;
      }

      .balance {
        font-family: var(--ng-font-bold);
        font-size: 22px;
        color: var(--ng-navy);
      }
    }

    .profile-sheet-wallet-actions {
      display: flex;
      gap: 8px;
    }
  }

  .ps-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: #f1f2fa;
    cursor: pointer;

    .v-icon {
      color: var(--ng-violet) !important;
    }

    .refresh-balance-icon {
      width: 20px;
      height: 20px;
      background-color: var(--ng-violet);
      mask: url(/static/image/profile/icon-refresh-type01.svg) no-repeat center / contain;
      -webkit-mask: url(/static/image/profile/icon-refresh-type01.svg) no-repeat center / contain;
    }

    &:hover {
      background: #e7e3fb;
    }
  }

  // VIP progress card
  .ps-vip {
    overflow: hidden;

    .ps-vip-main {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
    }

    .ps-vip-info {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .ps-vip-level {
      font-size: 15px;

      b {
        font-family: var(--ng-font-bold);
        color: var(--ng-violet);
        text-transform: uppercase;
      }
    }

    .ps-vip-progress-head {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
      font-size: 12px;
      color: #5b6080;

      span:last-child {
        font-family: var(--ng-font-bold);
        color: var(--ng-navy);
      }
    }

    .ps-vip-bar {
      background: #e7e9f3;
      border-radius: 999px;

      .v-progress-linear__background {
        opacity: 0 !important;
      }

      .v-progress-linear__determinate {
        background: var(--ng-gradient-brand) !important;
        border-radius: 999px;
      }

      &.bronze .v-progress-linear__determinate {
        background: linear-gradient(90deg, #eeaf88, #b75c2d) !important;
      }

      &.silver .v-progress-linear__determinate {
        background: linear-gradient(90deg, #d5d5d5, #9c9c9c) !important;
      }

      &.gold .v-progress-linear__determinate {
        background: linear-gradient(90deg, #f0b43b, #b56923) !important;
      }

      &.platinum .v-progress-linear__determinate {
        background: linear-gradient(90deg, #93d1e8, #468296) !important;
      }

      &.diamond .v-progress-linear__determinate {
        background: linear-gradient(90deg, #bb95ff, #6d4bae) !important;
      }

      &.black .v-progress-linear__determinate {
        background: var(--ng-gradient-cta) !important;
      }
    }

    .ps-vip-next {
      display: flex;
      flex-direction: column;
      font-size: 12px;
      line-height: 1.5;
      color: #5b6080;

      b {
        font-family: var(--ng-font-bold);
        color: var(--ng-navy);
      }
    }

    .ps-vip-badge {
      flex: none;
      width: 120px;
      height: auto;
      filter: drop-shadow(0 6px 10px rgba(18, 23, 52, 0.18));
    }

    .ps-vip-link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 16px;
      border: 0;
      border-top: 1px solid #eceef6;
      background: #fafbff;
      color: var(--ng-royal);
      font-family: var(--ng-font-bold);
      font-size: 13px;
      cursor: pointer;

      .v-icon {
        color: var(--ng-royal) !important;
      }

      &:hover {
        background: #f1f2fa;
      }
    }
  }

  // Guide strip: same violet info card as deposit and login
  .guideSectionProfile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 14px;
    background: #f1edff;
    border: 1px solid #e1d8ff;
    font-size: 13px;
    line-height: 1.4;

    .guide-icon {
      flex: none;
      color: var(--ng-violet) !important;
    }

    label {
      flex: 1 1 auto;
    }

    .ps-guide-close {
      flex: none;
      display: flex;
      border: 0;
      background: transparent;
      cursor: pointer;

      .v-icon {
        color: #9da1c0 !important;
      }
    }
  }

  // Referral shortcut
  .ps-referral {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border: 0;
    border-radius: 16px;
    background: var(--ng-gradient-brand);
    color: #ffffff;
    font-family: var(--ng-font-bold);
    font-size: 15px;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(54, 29, 210, 0.25);

    img {
      flex: none;
      padding: 4px;
      border-radius: 12px;
      background: #ffffff;
    }

    span {
      flex: 1 1 auto;
    }
  }

  // Menu sections
  .profile-sheet-item {
    padding: 14px 12px 8px;

    .profile-sheet-item-header {
      padding: 0 4px 6px;
    }

    .profile-sheet-item-title {
      position: relative;
      padding-left: 12px;
      font-family: var(--ng-font-bold);
      font-size: 15px;

      &::before {
        content: '';
        position: absolute;
        top: 3px;
        bottom: 3px;
        left: 0;
        width: 4px;
        border-radius: 2px;
        background: var(--ng-gradient-cta);
        transform: skewX(-12deg);
      }
    }

    .profile-sheet-item-divider {
      display: none;
    }

    .profile-sheet-item-row,
    .profile-sheet-item-row-mobile {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 4px;
    }

    .profile-sheet-item-column {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 10px 4px;
      border-radius: 12px;
      cursor: pointer;
      transition: background 0.2s ease-out;

      &:hover {
        background: #f5f6fb;
      }

      .profile-sheet-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        border-radius: 16px;
        background: #fff3e8;

        img {
          width: 32px;
          height: 32px;
          object-fit: contain;
        }
      }

      label {
        font-size: 12px;
        line-height: 1.3;
        text-align: center;
        cursor: pointer;
      }
    }

    .disbaledDiv {
      pointer-events: none;
      opacity: 0.4;
    }
  }

  // Logout
  .logout-container {
    display: flex;
    justify-content: center;
    padding: 4px;

    .logout-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      min-height: 48px;
      border-radius: 12px;
      color: #e53935;
      font-family: var(--ng-font-bold);
      cursor: pointer;

      &:hover {
        background: #fff5f5;
      }

      .logout-icon {
        width: 22px;
        height: 22px;
        background-color: #e53935;
        mask: url(/static/image/profile/icon-logout.svg) no-repeat center / contain;
        -webkit-mask: url(/static/image/profile/icon-logout.svg) no-repeat center / contain;
      }
    }
  }
}

@media (min-width: 600px) {
  .profile-sheet .profile-sheet-item {
    .profile-sheet-item-row,
    .profile-sheet-item-row-mobile {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
  }
}

@media (max-width: 400px) {
  .profile-sheet {
    .ps-vip .ps-vip-badge {
      width: 84px;
    }

    .profile-sheet-item .profile-sheet-item-column {
      .profile-sheet-icon {
        width: 46px;
        height: 46px;
      }

      label {
        font-size: 11px;
      }
    }
  }
}
</style>
